import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PARTICIPATING_PARAM,
  PARTICIPATING_STATUS_PARAM,
} from "@/constants/prd-search-params";
import { useTabParam } from "@/hooks/tab-param";
import PrdTable from "@/screens/Prds/prd-table";
import type { Prd } from "@/types/domain";

// "Participating" identities (PRD 27701e64): owner ∪ member ∪ follower,
// shown as separate tabs rather than one merged list — a merged list can't
// explain why a row is there. Each tab maps to one /prds filter. Created
// was dropped (owner 2026-09-08): it reads the same as My PRDs.
export type ParticipatingTab = "owned" | "member" | "following";

const IDENTITIES: {
  key: ParticipatingTab;
  label: string;
  param: "ownerId" | "memberId" | "followerId";
}[] = [
  { key: "owned", label: "Owned", param: "ownerId" },
  { key: "member", label: "Member", param: "memberId" },
  { key: "following", label: "Following", param: "followerId" },
];

// Tab-label count: the same pageSize-1 probe PrdTable issues for its Open
// tab, so SWR dedupes the active tab's pair.
const useOpenCount = (params: Record<string, string>) => {
  const { data } = useSWR<{ data: Prd[]; total: number }>(
    swrKey("/prds", { ...params, pageIndex: 0, pageSize: 1 })
  );
  return data?.total;
};

const TabLabel = ({
  label,
  params,
}: {
  label: string;
  params: Record<string, string>;
}) => {
  const total = useOpenCount(params);
  return (
    <>
      {label}
      {total === undefined ? "" : ` (${total})`}
    </>
  );
};

/**
 * Segmented tabs over the PRDs a user participates in, one PrdTable per
 * identity. Selected tab lives in ?participating= so a detail round-trip
 * lands back on the same tab. `includeOwned` adds Owned first (user page);
 * the home dashboard leaves it out because My PRDs already is that view.
 */
export const ParticipatingTabs = ({
  userId,
  includeOwned,
  breadcrumb,
}: {
  userId: string;
  includeOwned?: boolean;
  breadcrumb?: { label?: string; to?: string }[];
}) => {
  const tabs = includeOwned
    ? IDENTITIES
    : IDENTITIES.filter((t) => t.key !== "owned");
  const defaultTab = tabs[0].key;
  const [param, setTab] = useTabParam(PARTICIPATING_PARAM, defaultTab);
  const active = tabs.find((t) => t.key === param) ?? tabs[0];

  return (
    <div className="space-y-2">
      <Tabs value={active.key} onValueChange={setTab}>
        <TabsList>
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              <TabLabel label={t.label} params={{ [t.param]: userId }} />
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {/* Keyed so the table's loading state / SWR subscription restarts per
          tab (pagination lives in a persisted store, not in the component).
          Own All/My Open/My Done param + pagination store: on home this table sits
          beside My PRDs, which owns ?status= and the "prd" store. */}
      <PrdTable
        key={active.key}
        params={{ [active.param]: userId }}
        hideCreate
        breadcrumb={breadcrumb}
        statusParam={PARTICIPATING_STATUS_PARAM}
        storeKey="prd-participating"
      />
    </div>
  );
};

export default ParticipatingTabs;
